import React from "react";
import { Box, Typography, Paper, Grid } from "@mui/material";
import SecurityIcon from "@mui/icons-material/Security";
import NoteAddIcon from "@mui/icons-material/NoteAdd";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import SavingsIcon from "@mui/icons-material/Savings";
import PieChartIcon from "@mui/icons-material/PieChart";
import HistoryEduIcon from "@mui/icons-material/HistoryEdu";
import GroupsIcon from "@mui/icons-material/Groups";
import BugReportIcon from "@mui/icons-material/BugReport";

const Dashboard = ({ metrics }) => {
  const formatCurrency = (val) => {
    return new Intl.NumberFormat("it-IT", {
      style: "currency",
      currency: "EUR",
    }).format(val);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <Grid container spacing={2}>
        {/* KPI 1: Media Garanzie Totale */}
        <Grid item xs={12} md={6} lg={4}>
          <MetricCard
            title="Media Garanzie Totale"
            value={metrics.avgGarGlobal.toFixed(3)}
            subValue="Auto / Moto / Van"
            icon={<SecurityIcon fontSize="small" />}
            color="info"
            trend="neutral"
          />
        </Grid>

        {/* KPI 2: Media Garanzie Nuove (RISCHIO) */}
        <Grid item xs={12} md={6} lg={4}>
          <MetricCard
            title="Media Garanzie - Nuove"
            value={metrics.avgGarNew.toFixed(2)}
            subValue="Rischi Nuovi"
            icon={<NoteAddIcon fontSize="small" />}
            color="success"
            trend={metrics.avgGarNew >= 1.9 ? "success" : "danger"}
          />
        </Grid>

        {/* KPI 3: Media Garanzie Rinnovi (RINNOVO) */}
        <Grid item xs={12} md={6} lg={4}>
          <MetricCard
            title="Media Garanzie - Rinnovi"
            value={metrics.avgGarRen.toFixed(2)}
            subValue="Rinnovi Portafoglio"
            icon={<AutorenewIcon fontSize="small" />}
            color="secondary"
            trend={metrics.avgGarRen >= 1.4 ? "success" : "danger"}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={4} lg={2.4}>
          <MetricCard
            title="Volume Produzione"
            value={formatCurrency(metrics.totalVolume)}
            icon={<TrendingUpIcon fontSize="small" />}
            color="secondary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2.4}>
          <MetricCard
            title="Bonus Sconto Totale"
            value={formatCurrency(metrics.totalBonusSconto)}
            subValue="Post-Deduplicazione"
            icon={<SavingsIcon fontSize="small" />}
            color="primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2.4}>
          <MetricCard
            title="Netto Non Motor"
            value={formatCurrency(metrics.totalNetNonMotor)}
            subValue={`Mix: ${metrics.crossSellPerc.toFixed(1)}%`}
            icon={<PieChartIcon fontSize="small" />}
            color="secondary"
            trend={metrics.crossSellPerc >= 1.3 ? "success" : "danger"}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2.4}>
          <MetricCard
            title="Numero Polizze"
            value={metrics.totalPolizze.toString()}
            subValue="Escl. Riferimenti *"
            icon={<HistoryEduIcon fontSize="small" />}
            color="secondary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2.4}>
          <MetricCard
            title="Ritenzione Media"
            value={`${metrics.avgRetention.toFixed(1)}%`}
            icon={<GroupsIcon fontSize="small" />}
            color="success"
            trend={metrics.avgRetention >= 70 ? "success" : "danger"}
          />
        </Grid>
      </Grid>

      {/* Controllo Calcolo Debug Box */}
      <Paper
        sx={{
          borderRadius: 3,
          p: 2,
          border: 1,
          borderColor: "secondary.light",
          fontSize: "0.625rem",
          fontFamily: "monospace",
        }}
        elevation={0}
      >
        <Typography
          sx={{
            fontWeight: "bold",
            textTransform: "uppercase",
            letterSpacing: "0.1em",
            mb: 1,
            display: "flex",
            alignItems: "center",
            gap: 1,
            fontSize: "0.6875rem",
          }}
        >
          <BugReportIcon fontSize="small" /> Controllo Calcolo (Iron Gate Debug)
        </Typography>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
            alignItems: "center",
          }}
        >
          <Typography variant="inherit">
            Totale Righe: <Box component="span">{metrics.debug.total}</Box>
          </Typography>
          <Typography variant="inherit">→</Typography>
          <Typography variant="inherit">
            F1 (Auto/Moto/Van):{" "}
            <Box component="span">{metrics.debug.postF1}</Box>
          </Typography>
          <Typography variant="inherit">→</Typography>
          <Typography variant="inherit">
            F2 (No Asterischi):{" "}
            <Box component="span">{metrics.debug.postF2}</Box>
          </Typography>
          <Typography variant="inherit">→</Typography>
          <Typography variant="inherit">
            F3 (Emissioni):{" "}
            <Box
              component="span"
              sx={{ color: "success.light", fontWeight: "bold" }}
            >
              {metrics.debug.postF3}
            </Box>
          </Typography>
          <Box
            sx={{ width: "1px", height: 16, bgcolor: "secondary.light", mx: 1 }}
          ></Box>
          <Typography variant="inherit">
            Subset RISCHIO:{" "}
            <Box component="span" sx={{ color: "info.light" }}>
              {metrics.debug.countRischio}
            </Box>
          </Typography>
          <Typography variant="inherit">
            Subset RINNOVO:{" "}
            <Box component="span" sx={{ color: "info.main" }}>
              {metrics.debug.countRinnovo}
            </Box>
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

const MetricCard = ({
  title,
  value,
  subValue,
  icon,
  color,
  trend,
  customContent,
}) => {
  return (
    <Paper
      sx={{
        bgcolor: "background.paper",
        p: 2.5,
        borderRadius: 4,
        boxShadow: 1,
        border: 1,
        borderColor: "grey.200",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        transition: "all 0.3s",
        "&:hover": { boxShadow: 3, transform: "translateY(-4px)" },
        height: "100%",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          mb: 2,
        }}
      >
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 3,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            bgcolor: `${color}.light`,
            color: `${color}.dark`,
            opacity: 0.8,
          }}
        >
          {icon}
        </Box>
        {trend && trend !== "neutral" && (
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              bgcolor: trend === "success" ? "success.main" : "error.main",
              animation:
                trend === "success"
                  ? "pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite"
                  : "none",
            }}
          ></Box>
        )}
      </Box>
      <Box>
        <Typography
          sx={{
            fontSize: "0.5625rem",
            fontWeight: 900,
            color: "grey.400",
            textTransform: "uppercase",
            letterSpacing: "0.1em",
            mb: 0.5,
          }}
        >
          {title}
        </Typography>
        <Typography
          sx={{
            fontSize: "1.125rem",
            fontWeight: 900,
            color: "text.primary",
            letterSpacing: "-0.05em",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {value}
        </Typography>
        {subValue && (
          <Typography
            sx={{
              fontSize: "0.5625rem",
              fontWeight: "bold",
              color: "text.secondary",
              mt: 0.5,
              textTransform: "uppercase",
              letterSpacing: "-0.02em",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {subValue}
          </Typography>
        )}
        {customContent}
      </Box>
    </Paper>
  );
};

export default Dashboard;
